/** Folder moves also carry project mount edges under their path (RFC 0357 S4). */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { projectSources } from '../code/sources'
import {
  createFolder,
  moveFolder,
  normalizeDocumentPath,
  removeEmptyFolder,
} from '../documents/mutations'
import { projectFolders } from '../documents/reads'
import { changeProject } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import { ensureWorldProjects, projectUrl } from './mutations'
import { worldProjectMounts } from './reads'
import type { WorldDoc } from './types'

export async function moveFolderWithProjects(
  repo: Repo,
  worldId: string,
  projectId: string,
  from: string,
  to: string,
  opts: { expectIds?: readonly string[]; merge?: boolean } = {},
): Promise<{ moved: string[]; projects: string[] }> {
  const src = normalizeDocumentPath(from)
  const dst = normalizeDocumentPath(to)
  if (!src || !dst) throw new Error('not a folder path')
  if (src === dst) return { moved: [], projects: [] }
  if (dst.startsWith(`${src}/`))
    throw new Error('cannot move a folder into itself')

  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  const mounts = worldProjectMounts(world.doc())
  const shifted = mounts
    .filter(
      (mount) =>
        mount.parentProjectId === projectId &&
        (mount.folder === src || mount.folder.startsWith(`${src}/`)),
    )
    .map((mount) => ({
      projectId: mount.projectId,
      folder: `${dst}${mount.folder.slice(src.length)}`,
    }))
  const shiftedIds = new Set(shifted.map((mount) => mount.projectId))
  const occupied = new Set(
    mounts
      .filter(
        (mount) =>
          mount.parentProjectId === projectId &&
          !shiftedIds.has(mount.projectId),
      )
      .map((mount) => mount.folder),
  )
  for (const mount of shifted) {
    if (occupied.has(mount.folder))
      throw new Error(`${mount.folder} already holds a project`)
    occupied.add(mount.folder)
  }

  const parent = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (parent as { whenReady?: () => Promise<void> }).whenReady?.()
  const sources = projectSources(parent.doc()).sources
  const explicitFolders = projectFolders(parent.doc())
  const shiftedFolderCount = explicitFolders.filter(
    (folder) => folder.path === src || folder.path.startsWith(`${src}/`),
  ).length
  const shiftedSources = sources
    .filter(
      (source) => source.folder === src || source.folder?.startsWith(`${src}/`),
    )
    .map((source) => ({
      id: source.id,
      folder: `${dst}${source.folder!.slice(src.length)}`,
    }))
  const shiftedSourceIds = new Set(shiftedSources.map((source) => source.id))
  for (const source of shiftedSources) {
    if (
      sources.some(
        (other) =>
          !shiftedSourceIds.has(other.id) &&
          other.folder &&
          (other.folder === source.folder ||
            other.folder.startsWith(`${source.folder}/`) ||
            source.folder.startsWith(`${other.folder}/`)),
      ) ||
      mounts.some(
        (mount) =>
          mount.parentProjectId === projectId &&
          !shiftedIds.has(mount.projectId) &&
          (mount.folder === source.folder ||
            mount.folder.startsWith(`${source.folder}/`) ||
            source.folder.startsWith(`${mount.folder}/`)),
      )
    )
      throw new Error(`${source.folder} overlaps a source or project`)
  }
  const documentIds = Object.entries(parent.doc()?.documents ?? {})
    .filter(([, ref]) =>
      normalizeDocumentPath(String(ref.path))?.startsWith(`${src}/`),
    )
    .map(([id]) => id)
  const movedIds = new Set(documentIds)
  for (const [id, ref] of Object.entries(parent.doc()?.documents ?? {})) {
    if (movedIds.has(id)) continue
    const path = normalizeDocumentPath(String(ref.path))
    if (
      shifted.some(
        (mount) =>
          path === mount.folder || path?.startsWith(`${mount.folder}/`),
      )
    )
      throw new Error('the moved project would collide with a document')
    if (
      shiftedSources.some(
        (source) =>
          path === source.folder ||
          path?.startsWith(`${source.folder}/`) ||
          source.folder.startsWith(`${path}/`),
      )
    )
      throw new Error('the moved source would collide with a document')
  }
  if (
    documentIds.length === 0 &&
    shiftedFolderCount === 0 &&
    shifted.length === 0 &&
    shiftedSources.length === 0
  )
    throw new Error(`no documents, folders, projects or sources under ${src}/`)
  if (opts.expectIds) {
    const expected = new Set(opts.expectIds)
    if (
      expected.size !== documentIds.length ||
      documentIds.some((id) => !expected.has(id))
    )
      throw new Error(
        `${src}/ changed while the move was being checked; try again`,
      )
  }
  const moved =
    documentIds.length || shiftedFolderCount
      ? (await moveFolder(repo, projectId, src, dst, opts)).moved
      : []
  if (shiftedSources.length) {
    changeProject(parent, (draft) => {
      for (const source of shiftedSources) {
        if (draft.sources?.[source.id])
          draft.sources[source.id]!.folder = source.folder
      }
    })
  }
  if (shifted.length) {
    world.change((draft: WorldDoc) => {
      const projects = ensureWorldProjects(draft)
      for (const mount of shifted) {
        const entry = projects[projectUrl(mount.projectId)]
        if (entry) {
          if (entry.parent) entry.parent.folder = mount.folder
          else
            entry.parent = {
              projectId: projectId as never,
              folder: mount.folder,
            }
        }
      }
    })
  }
  return { moved, projects: shifted.map((mount) => mount.projectId) }
}

/** A project mount owns its path, so ordinary folders cannot be made inside it. */
export async function createFolderInWorld(
  repo: Repo,
  worldId: string,
  projectId: string,
  path: string,
): ReturnType<typeof createFolder> {
  const target = normalizeDocumentPath(path)
  if (!target) throw new Error('not a folder path')
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  if (
    worldProjectMounts(world.doc()).some(
      (mount) =>
        mount.parentProjectId === projectId &&
        (target === mount.folder || target.startsWith(`${mount.folder}/`)),
    )
  )
    throw new Error(`${target} belongs to a project`)
  return createFolder(repo, projectId, target)
}

/** Refuse removal if a nested project is the folder's last child. */
export async function removeEmptyFolderInWorld(
  repo: Repo,
  worldId: string,
  projectId: string,
  path: string,
): Promise<void> {
  const target = normalizeDocumentPath(path)
  if (!target) throw new Error('not a folder path')
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  if (
    worldProjectMounts(world.doc()).some(
      (mount) =>
        mount.parentProjectId === projectId &&
        (mount.folder === target || mount.folder.startsWith(`${target}/`)),
    )
  )
    throw new Error(`${target} is not empty`)
  await removeEmptyFolder(repo, projectId, target)
}
